import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Divider,
  Rating,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  ArrowBack as ArrowBackIcon,
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  Kitchen as KitchenIcon,
  AcUnit as AcIcon,
  Pool as PoolIcon,
  FitnessCenter as GymIcon,
  Pets as PetsIcon,
  SmokeFree as NoSmokingIcon,
} from '@mui/icons-material';
import { Layout } from '@/components/Layout';
import { useSearch, PublicListing } from '@/contexts/SearchContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/utils/api-client';
import ImageCarousel from '@/components/Search/ImageCarousel';
import BookingInquiryWidget from '../components/Search/BookingInquiryWidget';
import { GoogleMapsDisplay } from '@/components/common/GoogleMapsDisplay';
import { HostProfileCard } from '@/components/common/HostProfileCard';
import { ReviewDisplayComponent } from '@/components/Reviews/ReviewDisplayComponent';
import { RatingSummaryWidget } from '@/components/Reviews/RatingSummaryWidget';
import { Review, ReviewResponseData } from '@/types/review-types';

// Amenity icon mapping
const amenityIcons: Record<string, React.ReactElement> = {
  'WiFi': <WifiIcon />,
  'Parking': <ParkingIcon />,
  'Kitchen': <KitchenIcon />,
  'Air Conditioning': <AcIcon />,
  'Pool': <PoolIcon />,
  'Gym': <GymIcon />,
  'Pet Friendly': <PetsIcon />,
  'No Smoking': <NoSmokingIcon />,
};

const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const { isFavorite, toggleFavorite } = useSearch();
  const { isAuthenticated, userProfile } = useAuth();
  
  const [listing, setListing] = useState<PublicListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);

  // Load listing data
  useEffect(() => {
    const loadListing = async () => {
      if (!id) {
        setError('Property ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Make actual API call to get listing details using API client
        const response = await apiClient.get<PublicListing>(`/public/listings/${id}`);
        setListing(response.data);
      } catch (err) {
        console.error('Error loading listing:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load property details';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadListing();
  }, [id]);

  // Load reviews for the listing
  useEffect(() => {
    const loadReviews = async () => {
      if (!id) return;

      try {
        setReviewsLoading(true);
        setReviewsError(null);
        
        // Make API call to get reviews for this listing
        const response = await apiClient.get<{ reviews: Review[] }>(`/reviews/listing/${id}`);
        setReviews(response.data.reviews || []);
      } catch (err) {
        console.error('Error loading reviews:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load reviews';
        setReviewsError(errorMessage);
      } finally {
        setReviewsLoading(false);
      }
    };

    loadReviews();
  }, [id]);

  // Handle review response submission
  const handleReviewResponse = async (reviewId: string, responseData: ReviewResponseData) => {
    try {
      await apiClient.post(`/reviews/${reviewId}/response`, responseData);
      
      // Reload reviews to show the new response
      const response = await apiClient.get<{ reviews: Review[] }>(`/reviews/listing/${id}`);
      setReviews(response.data.reviews || []);
    } catch (err) {
      console.error('Error submitting review response:', err);
      throw err;
    }
  };

  // Handle favorite toggle
  const handleFavoriteClick = useCallback(() => {
    if (!listing) return;
    toggleFavorite(listing.listingId);
  }, [listing, toggleFavorite]);

  // Handle share
  const handleShare = useCallback(async () => {
    if (!listing) return;
    
    const shareData = {
      title: listing.title,
      text: `Check out this amazing property: ${listing.title}`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        // You could show a toast notification here
        console.log('Link copied to clipboard');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  }, [listing]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Format location display
  const getLocationText = () => {
    if (!listing) return '';
    const { address } = listing;
    if (address.neighborhood && address.city) {
      return `${address.neighborhood}, ${address.city}, ${address.state}`;
    }
    if (address.city && address.state) {
      return `${address.city}, ${address.state}, ${address.country}`;
    }
    return `${address.city || address.state}, ${address.country}`;
  };

  // Get property features
  const getPropertyFeatures = () => {
    if (!listing) return '';
    const features = [];
    if (listing.maxGuests) features.push(`${listing.maxGuests} guests`);
    if (listing.bedrooms) features.push(`${listing.bedrooms} bedroom${listing.bedrooms > 1 ? 's' : ''}`);
    if (listing.bathrooms) features.push(`${listing.bathrooms} bathroom${listing.bathrooms > 1 ? 's' : ''}`);
    return features.join(' • ');
  };

  // Get visible amenities (show first 6, then expand)
  const getVisibleAmenities = () => {
    if (!listing) return [];
    return showAllAmenities ? listing.amenities : listing.amenities.slice(0, 6);
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Header skeleton */}
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={300} height={24} sx={{ mt: 1 }} />
          </Box>
          
          {/* Image gallery skeleton */}
          <Box sx={{ mb: 4 }}>
            <Skeleton
              variant="rectangular"
              height={isMobile ? 300 : 400}
              sx={{ borderRadius: 3 }}
            />
          </Box>
          
          {/* Content skeleton */}
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Skeleton variant="text" width="80%" height={32} />
              <Skeleton variant="text" width="60%" height={24} sx={{ mt: 1 }} />
              <Skeleton variant="rectangular" height={200} sx={{ mt: 3, borderRadius: 2 }} />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
            </Grid>
          </Grid>
        </Container>
      </Layout>
    );
  }

  // Error state
  if (error || !listing) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Property not found'}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Go Back
          </Button>
        </Container>
      </Layout>
    );
  }

  const isListingFavorite = isFavorite(listing.listingId);

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={handleBack}
              sx={{ 
                mr: 1,
                minWidth: isMobile ? 44 : 'auto',
                minHeight: isMobile ? 44 : 'auto',
              }}
              aria-label="Go back"
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Box sx={{ flex: 1 }}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                component="h1"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                {listing.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <RatingSummaryWidget
                    entityId={listing.listingId}
                    entityType="listing"
                    compact={true}
                    loading={reviewsLoading}
                    error={reviewsError || undefined}
                  />
                </Box>
                
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <LocationIcon fontSize="small" />
                  {getLocationText()}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleShare}
                sx={{ 
                  color: 'text.secondary',
                  minWidth: isMobile ? 44 : 'auto',
                  minHeight: isMobile ? 44 : 'auto',
                }}
                aria-label="Share property"
              >
                <ShareIcon />
              </IconButton>
              
              <IconButton
                onClick={handleFavoriteClick}
                sx={{ 
                  color: isListingFavorite ? 'error.main' : 'text.secondary',
                  minWidth: isMobile ? 44 : 'auto',
                  minHeight: isMobile ? 44 : 'auto',
                }}
                aria-label={isListingFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                {isListingFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Image Gallery */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              height: isMobile ? 300 : 400,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            <ImageCarousel
              images={listing.photos}
              alt={listing.title}
              aspectRatio={isMobile ? 1.33 : 2}
              showDots={true}
              showArrows={true}
              borderRadius={12}
            />
          </Box>
        </Box>

        {/* Main Content */}
        <Grid container spacing={isMobile ? 2 : 4}>
          {/* Left Column - Property Details */}
          <Grid item xs={12} lg={8}>
            {/* Property Overview */}
            <Box sx={{ mb: isMobile ? 3 : 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography 
                    variant={isMobile ? 'h6' : 'h5'} 
                    fontWeight="bold" 
                    gutterBottom
                    sx={{ fontSize: isMobile ? '1.3rem' : '1.5rem' }}
                  >
                    {listing.roomType} hosted by {listing.hostInfo.name || listing.hostInfo.hostName || 'Host'}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary">
                    {getPropertyFeatures()}
                  </Typography>
                  
                  {listing.propertyType && (
                    <Chip
                      label={listing.propertyType}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                  
                  {listing.instantBook && (
                    <Chip
                      label="Instant Book"
                      size="small"
                      color="primary"
                      sx={{ mt: 1, ml: 1 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: isMobile ? 3 : 4 }} />

            {/* Description */}
            <Box sx={{ mb: isMobile ? 3 : 4 }}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.2rem' : '1.25rem' }}
              >
                About this place
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line',
                  fontSize: isMobile ? '1rem' : '0.875rem',
                }}
              >
                {listing.description}
              </Typography>
            </Box>

            <Divider sx={{ mb: isMobile ? 3 : 4 }} />

            {/* Amenities */}
            <Box sx={{ mb: isMobile ? 3 : 4 }}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.2rem' : '1.25rem' }}
              >
                What this place offers
              </Typography>
              
              <Grid container spacing={2}>
                {getVisibleAmenities().map((amenity, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                      {amenityIcons[amenity] || <Box sx={{ width: 24, height: 24 }} />}
                      <Typography variant="body1">
                        {amenity}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              
              {listing.amenities.length > 6 && (
                <Button
                  variant="outlined"
                  onClick={() => setShowAllAmenities(!showAllAmenities)}
                  sx={{ mt: 2 }}
                >
                  {showAllAmenities 
                    ? 'Show less' 
                    : `Show all ${listing.amenities.length} amenities`
                  }
                </Button>
              )}
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Location */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Where you'll be
              </Typography>
              
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {getLocationText()}
              </Typography>
              
              {listing.address.neighborhood && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Located in the vibrant {listing.address.neighborhood} neighborhood, 
                  you'll be close to restaurants, shops, and local attractions.
                </Typography>
              )}
              
              {/* Google Maps Display */}
              <Box sx={{ mt: 2 }}>
                <GoogleMapsDisplay
                  city={`${listing.address.city}, ${listing.address.state}, ${listing.address.country}`}
                  address={getLocationText()}
                  title={`Property in ${listing.address.city}`}
                  height={300}
                  zoom={12}
                  showMarker={true}
                  interactive={true}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Host Profile */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Meet your host
              </Typography>
              
              <HostProfileCard hostId={listing.hostInfo.hostId} />
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Reviews Section */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                gutterBottom
                sx={{ fontSize: isMobile ? '1.2rem' : '1.25rem' }}
              >
                Reviews
              </Typography>
              
              {/* Rating Summary */}
              <Box sx={{ mb: 4 }}>
                <RatingSummaryWidget
                  entityId={listing.listingId}
                  entityType="listing"
                  showBreakdown={true}
                  loading={reviewsLoading}
                  error={reviewsError || undefined}
                />
              </Box>
              
              {/* Reviews List */}
              <ReviewDisplayComponent
                reviews={reviews}
                showPagination={true}
                showResponses={true}
                allowResponse={!!(isAuthenticated && userProfile && listing.hostInfo.hostId === userProfile.userId)}
                currentUserId={userProfile?.userId}
                loading={reviewsLoading}
                error={reviewsError || undefined}
                onSubmitResponse={handleReviewResponse}
              />
            </Box>
          </Grid>

          {/* Right Column - Booking Widget */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ 
              position: isMobile ? 'static' : 'sticky', 
              top: isMobile ? 'auto' : 24,
              order: isMobile ? -1 : 0, // Show booking widget first on mobile
            }}>
              <BookingInquiryWidget listing={listing} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default PropertyDetailPage;